import { ChartConfigChartPaneElement, RendererChartBarProps, RequireByKey } from '../../types/shared'
import Text from '../canvas/text'
import { ChartElementType, LINE_HEIGHT, PADDING, ScaleType } from '../constants/common'
import Quote from '../models/quote'
import { getHSVAFromColor, stringifyHSVAColor } from '../utils/colors'
import CandleStick from './candle_stick'

type OhlcKeys = 'high' | 'low' | 'open' | 'close'

class HeikinAshi extends CandleStick {
  static type = ChartElementType.HeikinAshi
  static label = 'Heikin Ashi'
  static iconName = 'candleBarHeikinAshi'

  static getNumOfBarsBuffer(element: RequireByKey<ChartConfigChartPaneElement, 'overlays'>) {
    return Math.max(1, super.getNumOfBarsBuffer(element))
  }

  getRenderChartBarProps = ({ index, chartSettingsColors, hasFillAndBorder }: RendererChartBarProps) => {
    const haData = this.data.getHeikinAshiData()

    const open = haData.open[index]
    const close = haData.close[index]
    const trendColor = close < open ? chartSettingsColors.trendDown : chartSettingsColors.trendUp
    const wickColor = close < open ? chartSettingsColors.wickDown : chartSettingsColors.wickUp
    return {
      open,
      close,
      high: Math.max(this.data.high[index], open, close),
      low: Math.min(open, close, this.data.low[index]),
      barFillColor: trendColor,
      borderColor: close < open ? chartSettingsColors.borderDown : chartSettingsColors.borderUp,
      wickColor: hasFillAndBorder ? wickColor : trendColor,
    }
  }

  getLastValues = () => {
    const Colors = this.getCandleLikeChartsRenderingColors()
    const {
      open: lastOpen,
      close: lastClose,
      high: lastHigh,
      low: lastLow,
    } = this.getRenderChartBarProps({
      index: this.data.date.length - 1,
      chartSettingsColors: Colors,
    })

    return { lastOpen, lastClose, lastHigh, lastLow }
  }

  getRoundedLastData() {
    const lastOhlc = this.getLastValues()
    return {
      open: this.roundOhlc(lastOhlc.lastOpen),
      high: this.roundOhlc(lastOhlc.lastHigh),
      low: this.roundOhlc(lastOhlc.lastLow),
      close: this.roundOhlc(lastOhlc.lastClose),
    }
  }

  getYAxisLabelYCoordinate({ heikinLast, originalLast }: { heikinLast: number; originalLast: number }) {
    const { ChartSettings } = this.getChartLayoutSettings()
    const { lineHeight } = ChartSettings.right.axis.font
    const padding = Text.getMergedPropsWithDefaults('padding', ChartSettings.right.axis.font.padding)

    const direction = Math.sign(heikinLast - originalLast) || 1
    const offset = (lineHeight ?? LINE_HEIGHT.S) + (padding.top ?? PADDING.XXS) + (padding.bottom ?? PADDING.XXS)
    const heikinLastPosition = Math.round(this.fy(heikinLast))
    const originalLastPosition = Math.round(this.fy(originalLast))
    const offsetedY = heikinLastPosition + offset * direction

    return Math.abs(originalLastPosition - heikinLastPosition) > offset ? originalLastPosition : offsetedY
  }

  renderYAxis = (context: CanvasRenderingContext2D) => {
    const { lastClose } = this.getLastValues()
    super.renderYAxis(context, { lastClose })

    const { firstVisibleClose } = this.getMinMax()
    if (this.model.scale === ScaleType.Percentage && !firstVisibleClose) return

    const originalLastClose = this.data.lastClose
    const heikinLastCloseLabel = this.getYAxisLastCloseLabel({ lastClose, firstVisibleClose })
    const originalLastCloseLabel = this.getYAxisLastCloseLabel({ lastClose: originalLastClose, firstVisibleClose })

    if (heikinLastCloseLabel === originalLastCloseLabel) return

    this.renderYAxisLabel({
      context,
      yCoordinate: this.getYAxisLabelYCoordinate({
        heikinLast: lastClose,
        originalLast: originalLastClose,
      }),
      label: originalLastCloseLabel,
      background: stringifyHSVAColor(
        { ...getHSVAFromColor(this.getChartLayoutSettings().ChartSettings.general.Colors.currentBackground), s: 0.3 },
        'hex'
      ),
    })
  }

  renderCrossText = (context: CanvasRenderingContext2D, crossIndex: number) => {
    const Colors = this.getCandleLikeChartsRenderingColors()
    const getDataByCrossIndex = (key: keyof Quote, shouldReturnRoundedString = true) => {
      const barIndex = this.data.barIndex.findIndex((index) => index === crossIndex)
      if (barIndex > -1) {
        let value = null
        if (['high', 'low', 'open', 'close'].includes(key)) {
          const data = this.getRenderChartBarProps({ index: barIndex, chartSettingsColors: Colors })
          value = data[key as OhlcKeys]
        }
        value = value === null ? this.data.getDataByBarIndex(key, crossIndex) : value
        if (value !== null) {
          return shouldReturnRoundedString ? this.roundOhlc(value) : `${value}`
        }
      }
    }
    super.renderCrossText(context, crossIndex, getDataByCrossIndex)
  }

  toString() {
    return `HA Candle stick ${this.data.ticker}`
  }
}

export default HeikinAshi
